import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/compras/pedidos-compra-lista-page.tsx — Fase 13, Plano 17 (COMP-04, D-19, D-20).
// Lista "Pedidos de Compra": situação, itens com saldo e atraso vêm do servidor (derivados dos recebimentos);
// nada é calculado aqui. Filtros: Fornecedor, Situação (multi), Prazo (Todos, Atrasados, Próximos 7 dias, Período…) e
// e a caixa de concluídos/cancelados, todos resolvidos no servidor e espelhados na URL.
// Plano 13-19: "Novo pedido" (cabeçalho e estado vazio; só compras:gerenciar) e linha clicável + "Abrir" levando ao detalhe.
import { Fragment, useCallback, useEffect, useState, type FormEvent } from 'react';
import { CalendarX, CheckCircle2, ChevronDown, ChevronRight, ChevronsUpDown, CircleDashed, Clock, PackageOpen, Plus, RefreshCw, Search, X, XCircle } from 'lucide-react';
import type { ListaPedidosCompra, PedidoCompraResumo, SituacaoPedidoCompra } from '@pieltec/contracts';
import { FornecedorCombobox } from '../../components/compras/fornecedor-combobox';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Skeleton } from '../../components/ui/skeleton';
import { TablePagination } from '../../components/ui/table-pagination';
import { faixaUrgencia } from '../../lib/kanban/apresentacao';
import {
  OPCOES_PRAZO_PEDIDO,
  SITUACOES_FILTRO_PEDIDO,
  apresentacaoSituacaoPedido,
  contagemPedidos,
  escreverFiltrosPedidos,
  estadoDaListaPedidos,
  filtrosPedidosAtivos,
  filtrosPedidosVazios,
  lerFiltrosPedidos,
  montarQueryPedidosCompra,
  prazoDoPedido,
  textoAtraso,
  type FiltrosPedidosCompra,
  type IconeSituacaoPedido,
  type PrazoFiltroPedido,
} from '../../lib/compras/pedidos';
import { obterSessaoAtual } from '../../lib/session';
import { formatarData } from '../../lib/vendas/formatacao';
import { CampoSelect } from '../../components/ui/campo-select';

const TAMANHO_PADRAO = 50;
const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

const ICONES_SITUACAO: Record<IconeSituacaoPedido, typeof CircleDashed> = { CircleDashed, PackageOpen, CheckCircle2, XCircle };

function destinoDetalhe(id: string): string {
  return `/compras/pedidos/detalhe?id=${encodeURIComponent(id)}`;
}

// O router escuta `popstate`; despachar o evento é a navegação programática sem reload (mesmo padrão do PV).
function navegar(destino: string): void {
  window.history.pushState(null, '', destino);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function Atraso({ pedido, prazo }: { pedido: PedidoCompraResumo; prazo: 'ok' | 'perto' | 'estourado' }) {
  if (pedido.piorAtrasoDias !== null && pedido.piorAtrasoDias > 0 && prazo === 'estourado') {
    return (
      <span className="inline-flex items-center gap-1 text-rose-700">
        <CalendarX className="size-4 shrink-0" aria-hidden="true" />
        {textoAtraso(pedido.piorAtrasoDias)}
      </span>
    );
  }
  if (prazo === 'perto') {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700">
        <Clock className="size-4 shrink-0" aria-hidden="true" />
        Prazo próximo
      </span>
    );
  }
  return <span className="text-slate-500">—</span>;
}

export function PedidosCompraListaPage() {
  const [dados, setDados] = useState<ListaPedidosCompra | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosPedidosCompra>(() => lerFiltrosPedidos(window.location.search));
  const [buscaDigitada, setBuscaDigitada] = useState(filtros.busca);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PADRAO);
  const [expandidos, setExpandidos] = useState<ReadonlySet<string>>(new Set());
  const [podeGerenciar, setPodeGerenciar] = useState(false);

  // A autorização real é do servidor (compras:gerenciar); aqui o botão só aparece para quem pode.
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPodeGerenciar(sessao.autenticado && sessao.usuario.permissions.includes('compras:gerenciar'));
    });
    return () => {
      vivo = false;
    };
  }, []);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/compras/pedidos?${montarQueryPedidosCompra(filtros, { pagina, tamanhoPagina })}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaPedidosCompra;
        if (!sinal || sinal.vivo) setDados(json);
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [filtros, pagina, tamanhoPagina],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  // Filtros na URL: recarregar ou compartilhar o link preserva o recorte (replaceState não empilha histórico).
  useEffect(() => {
    const query = escreverFiltrosPedidos(filtros);
    const destino = `${window.location.pathname}${query ? `?${query}` : ''}`;
    if (destino !== `${window.location.pathname}${window.location.search}`) window.history.replaceState(null, '', destino);
  }, [filtros]);

  function alterarFiltros(parcial: Partial<FiltrosPedidosCompra>): void {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPagina(1);
  }

  function aplicarBusca(event: FormEvent): void {
    event.preventDefault();
    alterarFiltros({ busca: buscaDigitada.trim() });
  }

  function limparFiltros(): void {
    setBuscaDigitada('');
    setFiltros(filtrosPedidosVazios());
    setPagina(1);
  }

  function alternarSituacao(situacao: SituacaoPedidoCompra, marcada: boolean): void {
    alterarFiltros({ situacoes: marcada ? [...filtros.situacoes, situacao] : filtros.situacoes.filter((atual) => atual !== situacao) });
  }

  function alternarExpansao(id: string): void {
    setExpandidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  const total = dados?.total ?? 0;
  const comFiltro = filtrosPedidosAtivos(filtros);
  const resumoSituacoes =
    filtros.situacoes.length === 0
      ? 'Todas as situações'
      : filtros.situacoes.length === 1
        ? apresentacaoSituacaoPedido(filtros.situacoes[0] as SituacaoPedidoCompra).rotulo
        : `${filtros.situacoes.length} situações`;
  const estado = estadoDaListaPedidos({ erro, carregando, temDados: dados !== null, total, comFiltro });

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Pedidos de Compra</h1>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {dados ? contagemPedidos(dados.total) : ' '}
            </p>
          </div>
          {podeGerenciar && (
            <Button type="button" onClick={() => navegar('/compras/pedidos/novo')}>
              <Plus aria-hidden="true" />
              Novo pedido
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="sticky top-0 z-10 flex flex-col gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros da lista">
        <form onSubmit={aplicarBusca} role="search" className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              type="search"
              aria-label="Buscar pedidos de compra"
              placeholder="Buscar por nº do pedido ou fornecedor"
              value={buscaDigitada}
              onChange={(e) => setBuscaDigitada(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="secondary" disabled={estado === 'carregando'}>
            Buscar
          </Button>
        </form>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-[16rem] flex-col gap-1">
            <label htmlFor="filtro-fornecedor" className="text-xs font-medium text-slate-600">
              Fornecedor
            </label>
            <div className="flex items-center gap-1">
              <div className="flex-1">
                <FornecedorCombobox value={filtros.fornecedor} onChange={(fornecedor) => alterarFiltros({ fornecedor })} id="filtro-fornecedor" />
              </div>
              {filtros.fornecedor && (
                <Button type="button" variant="ghost" size="icon" aria-label="Limpar fornecedor" onClick={() => alterarFiltros({ fornecedor: null })}>
                  <X aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-600">Situação</span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  className="ui-control flex min-h-[var(--control-height,2rem)] min-w-[12rem] items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                >
                  <span className="truncate">{resumoSituacoes}</span>
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 text-slate-400" aria-hidden="true" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" aria-label="Situações">
                <div className="flex flex-col gap-1">
                  {SITUACOES_FILTRO_PEDIDO.map((situacao) => {
                    const idCampo = `filtro-situacao-${situacao}`;
                    return (
                      <label key={situacao} htmlFor={idCampo} className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-slate-100">
                        <Checkbox id={idCampo} checked={filtros.situacoes.includes(situacao)} onCheckedChange={(marcada) => alternarSituacao(situacao, marcada === true)} />
                        {apresentacaoSituacaoPedido(situacao).rotulo}
                      </label>
                    );
                  })}
                  {filtros.situacoes.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => alterarFiltros({ situacoes: [] })}>
                      Limpar situações
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="filtro-prazo" className="text-xs font-medium text-slate-600">
              Prazo
            </label>
            <CampoSelect
              id="filtro-prazo"
              value={filtros.prazo}
              onValueChange={(valor) => alterarFiltros({ prazo: valor as PrazoFiltroPedido })}
              options={OPCOES_PRAZO_PEDIDO.map((opcao) => ({ value: opcao.valor, label: opcao.rotulo }))}
              className="w-56"
            />
          </div>

          {filtros.prazo === 'PERIODO' && (
            <>
              <div className="flex flex-col gap-1">
                <label htmlFor="filtro-prazo-de" className="text-xs font-medium text-slate-600">
                  De
                </label>
                <DatePicker id="filtro-prazo-de" value={filtros.de} onValueChange={(value) => alterarFiltros({ de: value })} />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="filtro-prazo-ate" className="text-xs font-medium text-slate-600">
                  Até
                </label>
                <DatePicker id="filtro-prazo-ate" value={filtros.ate} onValueChange={(value) => alterarFiltros({ ate: value })} />
              </div>
            </>
          )}

          <label htmlFor="filtro-concluidos" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox id="filtro-concluidos" checked={filtros.incluirConcluidos} onCheckedChange={(marcada) => alterarFiltros({ incluirConcluidos: marcada === true })} />
            Incluir concluídos e cancelados
          </label>

          {comFiltro && (
            <Button type="button" variant="ghost" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {estado === 'erro' ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar os pedidos de compra. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : estado === 'carregando' ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando pedidos de compra">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : estado === 'vazio' ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum pedido de compra</h2>
            <p className="max-w-xl text-sm text-muted-foreground">Crie um pedido para um fornecedor. A NF de compra é vinculada a ele no recebimento.</p>
            {podeGerenciar && (
              <Button type="button" onClick={() => navegar('/compras/pedidos/novo')}>
                <Plus aria-hidden="true" />
                Novo pedido
              </Button>
            )}
          </CardContent>
        </Card>
      ) : estado === 'sem-resultado' ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum pedido encontrado</h2>
            <p className="text-sm text-muted-foreground">Ajuste ou limpe os filtros.</p>
            <Button type="button" variant="outline" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <table className="w-full text-sm">
            <caption className="sr-only">Pedidos de compra</caption>
            <thead className="bg-slate-100 text-left">
              <tr>
                <th scope="col" className={CABECALHO}>Pedido</th>
                <th scope="col" className={CABECALHO}>Fornecedor</th>
                <th scope="col" className={`${CABECALHO} hidden lg:table-cell`}>Data</th>
                <th scope="col" className={`${CABECALHO} hidden lg:table-cell`}>Prazo</th>
                <th scope="col" className={`${CABECALHO} hidden text-right lg:table-cell`}>Itens</th>
                <th scope="col" className={CABECALHO}>Situação</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Itens com saldo</th>
                <th scope="col" className={CABECALHO}>Atraso</th>
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">Abrir</span>
                </th>
                <th scope="col" className="w-10 px-1 py-2 lg:hidden">
                  <span className="sr-only">Detalhes</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {dados?.itens.map((pedido) => {
                const prazo = prazoDoPedido(pedido);
                const situacao = apresentacaoSituacaoPedido(pedido.situacao);
                const IconeSituacao = ICONES_SITUACAO[situacao.icone];
                const aberto = expandidos.has(pedido.id);
                return (
                  <Fragment key={pedido.id}>
                    <tr
                      className="cursor-pointer border-t hover:bg-slate-50"
                      onClick={(evento) => {
                        // Cliques em links e botões da própria linha seguem o seu caminho (Abrir, expandir).
                        if ((evento.target as HTMLElement).closest('a, button')) return;
                        navegar(destinoDetalhe(pedido.id));
                      }}
                    >
                      <td className="relative py-2 pl-5 pr-3 font-semibold tabular-nums">
                        <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1.5 ${faixaUrgencia({ nivel: 'normal', prazo })}`} />
                        {pedido.numero}
                      </td>
                      <td className="max-w-[20rem] truncate px-3 py-2" title={pedido.fornecedorRazaoSocial}>
                        {pedido.fornecedorRazaoSocial}
                      </td>
                      <td className="hidden px-3 py-2 tabular-nums lg:table-cell">{formatarData(pedido.data)}</td>
                      <td className="hidden px-3 py-2 tabular-nums lg:table-cell">{formatarData(pedido.prazoGeral)}</td>
                      <td className="hidden px-3 py-2 text-right tabular-nums lg:table-cell">{pedido.totalItens}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>
                          <IconeSituacao className="size-3.5 shrink-0" aria-hidden="true" />
                          {situacao.rotulo}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{pedido.itensComSaldo}</td>
                      <td className="px-3 py-2">
                        <Atraso pedido={pedido} prazo={prazo} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button asChild variant="outline" size="sm">
                          <a href={destinoDetalhe(pedido.id)} aria-label={`Abrir pedido ${pedido.numero}`}>
                            Abrir
                          </a>
                        </Button>
                      </td>
                      <td className="w-10 px-1 py-1 text-right lg:hidden">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-expanded={aberto}
                          aria-label={`${aberto ? 'Recolher' : 'Expandir'} detalhes do pedido ${pedido.numero}`}
                          className="size-11 sm:size-9"
                          onClick={() => alternarExpansao(pedido.id)}
                        >
                          {aberto ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                        </Button>
                      </td>
                    </tr>
                    {aberto && (
                      <tr className="border-t bg-slate-50 lg:hidden">
                        <td colSpan={10} className="px-5 py-3">
                          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                            <div className="col-span-2 sm:col-span-4">
                              <dt className="text-xs text-muted-foreground">Fornecedor</dt>
                              <dd className="break-words">{pedido.fornecedorRazaoSocial}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-muted-foreground">Data</dt>
                              <dd className="tabular-nums">{formatarData(pedido.data)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-muted-foreground">Prazo</dt>
                              <dd className="tabular-nums">{formatarData(pedido.prazoGeral)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-muted-foreground">Itens</dt>
                              <dd className="tabular-nums">{pedido.totalItens}</dd>
                            </div>
                          </dl>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={total}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
            itemLabel="pedidos"
          />
        </Card>
      )}
    </div>
  );
}
